import { 
    FIREWEATHER_API_URL, 
    isLocal, 
    ENV 
} from "../env";

import {
    authClient
} from '../okta-auth';

// ! Helpers

/**
 * Makes an async call for forecast summary data for the data table
 * @returns Either the response object or an error.
 */
function callSummary() {
    // Get the access token
    const { idToken, accessToken, refreshToken } = authClient.tokenManager.getTokensSync();

    const summaryUrl = `${FIREWEATHER_API_URL}/fire-weather-areas/forecast/summary`
    return new Promise((resolve, reject) => {
        $.ajax({
            url: summaryUrl,
            headers: {
                'Authorization': `Bearer ${accessToken.accessToken}`
            },
            method: "GET",
            dataType: "json",
            content: "application/json",
            success: function(response) {
                resolve(response.data);
            },
            error: function(xhr, status, error) {
                if(xhr.responseJSON) {
                    reject(xhr.responseJSON.error);
                } else {
                    console.error("Not a valid response");
                    reject(error);
                }
            }
        })
    })
}

/**
 * Makes an async call for forecast detail data for the data table
 * @returns Either the response object or an error.
 */
function callDetailed() {
    // Get the access token
    const { idToken, accessToken, refreshToken } = authClient.tokenManager.getTokensSync();

    const detailedUrl = `${FIREWEATHER_API_URL}/fire-weather-areas/forecast/detailed`;
    return new Promise((resolve, reject) => {
        $.ajax({
            method: "GET",
            url: detailedUrl,
            headers: {
                'Authorization': `Bearer ${accessToken.accessToken}`
            },
            dataType: "json",
            contentType: "application/json",
            success: function (response) {
                resolve(response.data);
            },
            error: function (xhr, textStatus, error) {
                reject(error);
            }
        })
    })
}

/**
 * Makes an async call for forecast weather data for the data table
 * @returns Either the response object or an error.
 */
function callWeather() {
    const { idToken, accessToken, refreshToken } = authClient.tokenManager.getTokensSync();

    const weatherUrl = `${FIREWEATHER_API_URL}/fire-weather-areas/forecast/weather`;
    return new Promise((resolve, reject) => {
        $.ajax({
            method: "GET",
            url: weatherUrl,
            headers: {
                'Authorization': `Bearer ${accessToken.accessToken}`
            },
            dataType: 'json',
            contentType: 'application/json',
            success: function (response) {
                resolve(response.data);
            },
            error: function(xhr, textStatus, error) {
                reject(error);
            }
        });
    });
}

/**
 * Fetches all the data required to build the table.
 * @returns A promise that should resolve to the table data.
 */
const fetchTableData = async (forecastType) => {
    const data = await new Promise((resolve, reject) => {
        Promise.all([
            callSummary(),
            callDetailed(),
            callWeather(),
        ]).then((results) => {
            const summaryResult = results[0];
            const detailedResult = results[1];
            const weatherResult = results[2];
            resolve({
                summary: summaryResult,
                detailed: detailedResult,
                weather: weatherResult
            });
        }).catch((error) => {
            console.error(error);
            reject(error);
        })
    });

    data.summary = data.summary.filter(
        (obj) => obj.forecast_type === forecastType
    );

    data.weather = data.weather.filter(
        (obj) => obj.forecast_type === forecastType
    );

    data.detailed = data.detailed.filter(
        (obj) => obj.forecast_type === forecastType
    );

    data.summary = data.summary.sort((a,b) => {
        const timeA = new Date(a.forecast_start);
        const timeB = new Date(b.forecast_start);
        return Math.abs(timeA) - Math.abs(timeB);
    });

    data.weather = data.weather.sort((a,b) => {
        const timeA = new Date(a.forecast_start);
        const timeB = new Date(b.forecast_start);
        return Math.abs(timeA) - Math.abs(timeB);
    });
    
    data.detailed = data.detailed.sort((a,b) => {
        const timeA = new Date(a.forecast_start);
        const timeB = new Date(b.forecast_start);
        return Math.abs(timeA) - Math.abs(timeB);
    });

    return data;
}

// ! Exported functions

/**
 * Fetches the summary, detailed, and weather table data (in that order)
 * filtered by the `DailyForecast` id
 * @returns 
 */
export const districtFetchTableData = async () => {
    return await fetchTableData('DailyForecast');
}

/**
 * Fetches the summary, detailed, and weather table data (in that order)
 * filtered by the `PreliminaryForecast` id
 * @returns 
 */
export const preliminaryFetchTableData = async () => {
    return await fetchTableData('PreliminaryForecast');
}